/** Rivers, icons and the screenshot overlay (TD §5 setOverlay, calibrateOverlay; plus add/delete for the editor tools). */
import type { IconId, IconType, Overlay, PlanDoc, Pt, RiverId } from '../../shared/types';
import { clamp, dist, nearestOnPoly, SEG } from '../geom/basic';
import { riverPoly } from '../geom/shapes';
import { ok, refuse, type OpResult } from './result';
import { clampToMap, freshId, RIVER_W_MAX, RIVER_W_MIN } from './util';

/** Width of a newly drawn river in metres (the prototype's default). */
export const DEFAULT_RIVER_W = 12;

/** Adds a river through `points` (at least 2 distinct). */
export function addRiver(draft: PlanDoc, args: { points: readonly Pt[]; w?: number }): OpResult<RiverId> {
  const pts = args.points.filter((p, i) => i === 0 || dist(p, args.points[i - 1] as Pt) > 0.01).map((p): Pt => [p[0], p[1]]);
  if (pts.length < 2) return refuse('A river needs at least 2 points.');
  const id = freshId(draft.rivers);
  draft.rivers[id] = { pts, w: clamp(args.w ?? DEFAULT_RIVER_W, RIVER_W_MIN, RIVER_W_MAX) };
  return ok(id);
}

/** Deletes a river. */
export function deleteRiver(draft: PlanDoc, args: { riverId: RiverId }): OpResult {
  if (!draft.rivers[args.riverId]) return refuse('That river no longer exists.');
  delete draft.rivers[args.riverId];
  return ok(true);
}

/** Moves one point of a river (clamped to the map). */
export function moveRiverPoint(draft: PlanDoc, args: { riverId: RiverId; index: number; x: number; y: number }): void {
  const p = draft.rivers[args.riverId]?.pts[args.index];
  if (p) [p[0], p[1]] = clampToMap(draft, args.x, args.y);
}

/** Inserts a river point at (x, y), in the span nearest to it. Returns its index. */
export function insertRiverPoint(draft: PlanDoc, args: { riverId: RiverId; x: number; y: number }): OpResult<number> {
  const rv = draft.rivers[args.riverId];
  if (!rv) return refuse('That river no longer exists.');
  const q = nearestOnPoly(riverPoly(rv.pts), args.x, args.y);
  const at = clamp(Math.floor(q.i / SEG), 0, rv.pts.length - 2) + 1;
  rv.pts.splice(at, 0, clampToMap(draft, args.x, args.y));
  return ok(at);
}

/** Removes one river point. Refuses to leave fewer than 2 (R12). */
export function removeRiverPoint(draft: PlanDoc, args: { riverId: RiverId; index: number }): OpResult {
  const rv = draft.rivers[args.riverId];
  if (!rv || !rv.pts[args.index]) return refuse('That river point no longer exists.');
  if (rv.pts.length <= 2) return refuse('A river needs at least 2 points.');
  rv.pts.splice(args.index, 1);
  return ok(true);
}

/** Sets a river's width (clamped to 0.5–200 m). */
export function setRiverWidth(draft: PlanDoc, args: { riverId: RiverId; w: number }): void {
  const rv = draft.rivers[args.riverId];
  if (rv) rv.w = clamp(args.w, RIVER_W_MIN, RIVER_W_MAX);
}

/** Adds an icon (shop, toilets, entrance, …) at a spot on the map. */
export function addIcon(draft: PlanDoc, args: { type: IconType; x: number; y: number; fixed?: boolean }): IconId {
  const id = freshId(draft.icons), [x, y] = clampToMap(draft, args.x, args.y);
  draft.icons[id] = args.fixed ? { type: args.type, x, y, fixed: true } : { type: args.type, x, y };
  return id;
}

/** Deletes an icon. */
export function deleteIcon(draft: PlanDoc, args: { iconId: IconId }): OpResult {
  if (!draft.icons[args.iconId]) return refuse('That icon no longer exists.');
  delete draft.icons[args.iconId];
  return ok(true);
}

/** Sets or removes (null) the screenshot overlay. */
export function setOverlay(draft: PlanDoc, args: { overlay: Overlay | null }): void {
  draft.overlay = args.overlay ? { ...args.overlay, opacity: clamp(args.overlay.opacity, 0, 1) } : null;
}

/** Changes some overlay settings (position, scale, rotation, opacity) of the existing overlay. */
export function updateOverlay(draft: PlanDoc, args: Partial<Omit<Overlay, 'file'>>): OpResult {
  const o = draft.overlay;
  if (!o) return refuse('There is no screenshot under the map.');
  if (args.x !== undefined) o.x = args.x;
  if (args.y !== undefined) o.y = args.y;
  if (args.scale !== undefined && args.scale > 0) o.scale = args.scale;
  if (args.rotation !== undefined) o.rotation = args.rotation;
  if (args.opacity !== undefined) o.opacity = clamp(args.opacity, 0, 1);
  return ok(true);
}

/** Map position (metres) of an image pixel under the overlay's placement. */
export function overlayToMap(o: Overlay, u: number, v: number): Pt {
  const r = o.rotation * Math.PI / 180, c = Math.cos(r), s = Math.sin(r);
  return [o.x + o.scale * (u * c - v * s), o.y + o.scale * (u * s + v * c)];
}

/**
 * Calibrates the overlay scale (TD §5): `p1` and `p2` are two image pixels the user marked, `metres` the real
 * distance between them. The scale becomes metres per pixel. Image pixel p1 stays where it was on the map,
 * or lands on `anchor` (map metres) when given.
 */
export function calibrateOverlay(draft: PlanDoc, args: { p1: Pt; p2: Pt; metres: number; anchor?: Pt }): OpResult {
  const o = draft.overlay;
  if (!o) return refuse('There is no screenshot under the map.');
  const px = dist(args.p1, args.p2);
  if (!(px > 0) || !(args.metres > 0)) return refuse('Mark two different points and type a distance above 0.');
  const keep = args.anchor ?? overlayToMap(o, args.p1[0], args.p1[1]);
  o.scale = args.metres / px;
  const moved = overlayToMap(o, args.p1[0], args.p1[1]);
  o.x += keep[0] - moved[0];
  o.y += keep[1] - moved[1];
  return ok(true);
}
